import type { Tracker } from '../../types';
import {
    awaitAdvertisingPixels,
    createMissingPixelWarning,
} from '../advertising-pixels';
import {
    hasClientSpecificVariant,
    isLeadEvent,
} from '../../utils/conversionEvents';
import { getFacebookEvent } from '../facebook-tracker/utils';
import { getOpenAiEvent } from '../openai-tracker/utils';

declare global {
    interface Window {
        /**
         * Google Ads accounts the host app has configured on gtag, in `AW-…`
         * form. See `registerGoogleAdsAccounts`.
         */
        __apGoogleAdsAccounts?: Set<string>;
    }
}

/**
 * Record the Google Ads accounts gtag has been configured for.
 *
 * The host app calls this from whatever mounts its Google Ads tag, which is
 * also what consent gates. Conversions are reported only for accounts named
 * here, so suppressing the tag suppresses the conversions with it — `gtag`
 * itself cannot be used as the signal, because GA4 loads it either way.
 */
export const registerGoogleAdsAccounts = (accountIds: string[]): void => {
    const accounts = (window.__apGoogleAdsAccounts ??= new Set<string>());
    for (const accountId of accountIds) {
        accounts.add(accountId);
    }
};

/** The `AW-CONVERSION_ID` an `AW-CONVERSION_ID/CONVERSION_LABEL` belongs to. */
const conversionAccount = (sendTo: string): string => sendTo.split('/')[0];

/** Prefix for the copy of a conversion credited to the ad's professional. */
const CLIENT_SPECIFIC_PREFIX = 'ClientSpecific_';

/** Covers every lead type, so a campaign has one lead signal to bid on. */
const LEAD_CONVERSION = 'Lead';

/**
 * Conversion name to the Google Ads `send_to` value identifying the conversion
 * action, in `AW-CONVERSION_ID/CONVERSION_LABEL` form.
 */
export type GoogleAdsConversionLabels = Record<string, string>;

/**
 * Reports Google Ads conversions from the browser, paired with the server-side
 * copy the Data Manager API sends for the same conversion.
 *
 * Google merges the two copies only when both carry the same `transaction_id`,
 * which this sets to the event id. A broken deduplication contract fails
 * silently by doubling every conversion rather than erroring, so it lives in
 * code where tests cover it and a reviewer sees it in a diff.
 *
 * The conversion names derived here must stay identical to the forwarder's
 * `googleConversionNames`, because deduplication happens per conversion action:
 * a name the browser reports but the server does not, or the reverse, produces
 * an unmatched conversion rather than an error.
 *
 * Firing nothing when no labels are configured is what lets the code ship ahead
 * of the Google Ads setup: turning these on is a deliberate configuration step.
 */
export function createGoogleAdsTracker(
    conversionLabels: GoogleAdsConversionLabels | undefined,
): Tracker {
    const labels = conversionLabels ?? {};
    const configured = Object.keys(labels).length > 0;
    const warnPixelMissing = createMissingPixelWarning('Google Ads');

    return {
        name: 'googleAdsTracker',
        async log(event, opts) {
            // Without the id there is nothing to deduplicate against, so the
            // conversion would double rather than merge with the server copy.
            if (!configured || !opts.eventId) {
                return;
            }

            await awaitAdvertisingPixels();

            for (const name of googleAdsConversionNames(event, opts)) {
                const sendTo = labels[name];
                if (!sendTo) {
                    continue;
                }

                if (!isRegisteredAccount(conversionAccount(sendTo))) {
                    warnPixelMissing(name);
                    continue;
                }

                sendConversion(sendTo, opts.eventId);
            }
        },
        release() {
            // Conversions are sent as they happen, so there is nothing queued.
        },
    };
}

/**
 * Every Google Ads conversion name one event should be reported as.
 *
 * Expands the same two decisions the server forwarder expands — whether the
 * conversion belongs to the ad's professional, and whether it counts as a lead
 * — so both halves address the same conversion actions.
 */
export function googleAdsConversionNames(
    event: Parameters<Tracker['log']>[0],
    opts: Parameters<Tracker['log']>[1],
): string[] {
    const base = conversionBaseName(event);
    if (!base) {
        return [];
    }

    const names = [base];
    const clientSpecific =
        Boolean(opts.clientMatched) && hasClientSpecificVariant(event);

    if (clientSpecific) {
        names.push(`${CLIENT_SPECIFIC_PREFIX}${base}`);
    }

    if (isLeadEvent(event)) {
        names.push(LEAD_CONVERSION);

        if (clientSpecific) {
            names.push(`${CLIENT_SPECIFIC_PREFIX}${LEAD_CONVERSION}`);
        }
    }

    return names;
}

/**
 * The conversion name before the client-specific and lead copies are derived.
 *
 * Follows whichever vendor named the event, preferring Meta, which is the same
 * order the forwarder resolves it in. Anything neither vendor maps is not a
 * conversion we report.
 */
function conversionBaseName(
    event: Parameters<Tracker['log']>[0],
): string | undefined {
    return (
        getFacebookEvent(event)?.eventName ??
        getOpenAiEvent(event)?.eventName ??
        undefined
    );
}

/**
 * Whether the host app configured gtag for this account. False covers both the
 * consent-suppressed session and a conversion action pointing at an account the
 * app was never told about.
 */
function isRegisteredAccount(accountId: string): boolean {
    return window.__apGoogleAdsAccounts?.has(accountId) === true;
}

/**
 * Sends one conversion, keyed to the browser event id so Google can merge it
 * with the server copy of the same conversion.
 */
function sendConversion(sendTo: string, eventId: string): void {
    if (typeof window.gtag !== 'function') {
        return;
    }

    window.gtag('event', 'conversion', {
        send_to: sendTo,
        transaction_id: eventId,
    });
}
